import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { TrialRequestResponse } from "../../features/trial-requests/contracts/TrialRequestResponse";
import {
  selectTrialRequests,
  selectTrialRequestsListStatus,
} from "../../features/trial-requests/trial-requests-slice";
import {
  confirmTrialRequest,
  declineTrialRequest,
  deleteTrialRequest,
} from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";

const statusStyles: Record<TrialRequestResponse["status"], string> = {
  Pending: "bg-primary-95 text-primary-40 dark:bg-primary-20 dark:text-primary-90",
  Confirmed: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  Declined: "bg-grey-95 text-grey-40 dark:bg-primary-10 dark:text-grey-80",
};

/** The instructor's trial-request inbox with confirm/decline actions. */
const TrialRequestsList = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const requests = useAppSelector(selectTrialRequests);
  const listStatus = useAppSelector(selectTrialRequestsListStatus);

  if (listStatus === "loading" && requests.length === 0) {
    return <p className="text-sm text-grey-40 dark:text-grey-80">{t("common.loading", "Loading…")}</p>;
  }

  if (requests.length === 0) {
    return (
      <p className="text-sm text-grey-40 dark:text-grey-80">
        {t(
          "trialRequests.empty",
          "No trial requests yet. Share your booking page to receive some.",
        )}
      </p>
    );
  }

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" }).format(
      new Date(iso),
    );

  return (
    <ul className="flex flex-col gap-3">
      {requests.map((request) => (
        <li key={request.id} className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-medium text-grey-20 dark:text-white">
                {request.firstName} {request.lastName}
              </p>
              <p className="text-sm text-grey-40 dark:text-grey-80">
                {request.email}
                {request.phone && ` · ${request.phone}`} · {formatDate(request.dateCreated)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[request.status]}`}
              >
                {t(`trialRequests.status.${request.status}`, request.status)}
              </span>
              <button
                type="button"
                aria-label={t("trialRequests.delete", "Delete request")}
                title={t("trialRequests.delete", "Delete request")}
                className="text-grey-40 hover:text-red-700 dark:text-grey-80 dark:hover:text-red-400"
                onClick={() => dispatch(deleteTrialRequest(request.id))}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          {request.preferredTimes && (
            <p className="mt-2 text-sm text-grey-20 dark:text-white">
              <span className="text-grey-40 dark:text-grey-80">
                {t("trialRequests.preferredTimes", "Preferred times")}:
              </span>{" "}
              {request.preferredTimes}
            </p>
          )}
          {request.message && (
            <p className="mt-1 text-sm text-grey-20 dark:text-white">{request.message}</p>
          )}

          {request.status === "Pending" && (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => dispatch(confirmTrialRequest(request.id))}
              >
                {t("trialRequests.confirm", "Confirm & create member")}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => dispatch(declineTrialRequest(request.id))}
              >
                {t("trialRequests.decline", "Decline")}
              </button>
            </div>
          )}
          {request.status === "Confirmed" && request.convertedMemberId && (
            <Link
              to={`/members/${request.convertedMemberId}`}
              className="mt-3 inline-block text-sm text-primary-40 hover:underline dark:text-primary-90"
            >
              {t("trialRequests.openMember", "Open member")}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
};

export default TrialRequestsList;
